// The funnel: four stages, accounts leading and the second number changing meaning down it.
//
// Every stage is a button. The number is the answer to "how many"; the drawer behind it is the
// answer to "which ones", and a figure nobody can open is a figure nobody can check.
//
// Two rules the stages depend on and neither invents:
//
// * **The definition comes from the server.** `stage.definition` is written in `activity.py`
//   beside the code that satisfies it, so the sentence under a number and the rule that
//   produced it cannot drift apart.
// * **A zero renders as a zero.** Nothing here hides an empty stage. Met and Opportunity will
//   both read zero for a while -- every deal on the book predates its strategist's outreach,
//   and no meeting on an owned account has been Gong-recorded with its strategist on the call
//   yet -- and a stage that appeared one day without warning would be worse than a zero
//   somebody has been watching.

import type { FunnelStage } from '../api/types';
import { formatCount } from '../lib/format';

// Bars are drawn against the widest stage rather than against the book, so the shape shows the
// drop-off between stages. Against the book they would all be slivers and the funnel would
// read as a flat line -- which is a fact about how young the programme is, not about the work.
function barWidth(stage: FunnelStage, widest: number): string {
  if (!widest || !stage.accounts) return '0';
  return `${Math.max(2, Math.round((stage.accounts / widest) * 100))}%`;
}

// What the stage's second line says. Contacts at the top two, booked against attended in the
// middle, deals behind the stage-2 accounts at the bottom -- so the label is per stage rather
// than shared.
//
// **Booked and attended are never added.** They are the same meetings counted at two moments,
// and the gap between them is the thing worth looking at: three booked and none attended is
// either a month of meetings still to come or a month of no-shows, and both are worth knowing.
// The two disjoint credits -- theirs and influenced -- move to the drawer, which is where the
// question "whose meeting was it" actually gets asked.
function StageFigure({ stage }: { stage: FunnelStage }) {
  if (stage.key === 'met') {
    return (
      <span className="sub">
        {formatCount(stage.booked)} booked · {formatCount(stage.attended)} attended
      </span>
    );
  }
  // The deals behind the stage-2 account count and nothing else. Qualified pipeline is
  // deliberately absent: it windows on when a deal was *opened* while stage 2 windows on when
  // one *crossed*, so printing it here would put two dates on one line. Its own tile already
  // carries it.
  if (stage.key === 'opportunity') {
    return <span className="sub">{formatCount(stage.deals)} deals</span>;
  }
  return <span className="sub">{formatCount(stage.contacts)} contacts</span>;
}

// The conversion between one stage and the one above it, or the raw fraction when the base is
// too small for a percentage to mean anything.
//
// The threshold is the payload's `min_rate_denominator` -- the same number that decides whether
// a reply *rate* is printable. Borrowed deliberately rather than given its own constant: both
// answer "is this base big enough that a percentage is a fact rather than a shape", and one
// number nobody has to reconcile beats two that will drift. It bites hardest at the bottom,
// which is where it should: a meeting-per-reply percentage on single digits is exactly the kind
// of number people quote at each other.
function conversionNote(
  stage: FunnelStage,
  previous: FunnelStage | undefined,
  minDenominator: number,
): string | null {
  if (!previous || !previous.accounts) return null;
  if (previous.accounts < minDenominator) {
    return `${formatCount(stage.accounts)} of ${formatCount(previous.accounts)}`;
  }
  return `${Math.round((stage.accounts / previous.accounts) * 100)}% of ${previous.label.toLowerCase()}`;
}

export function Funnel({
  stages,
  minDenominator,
  openStage,
  onOpenStage,
}: {
  stages: FunnelStage[];
  minDenominator: number;
  openStage: string | null;
  // The stage's own account count travels with the key, so the drawer can say "the 200 most
  // recent of 431" without recounting a list it was handed already truncated.
  onOpenStage: (key: string, total: number) => void;
}) {
  const widest = Math.max(...stages.map(stage => stage.accounts), 0);

  return (
    <div className="stagefunnel">
      {stages.map((stage, index) => {
        const conversion = conversionNote(stage, stages[index - 1], minDenominator);
        return (
          <div key={stage.key}>
            {conversion ? <div className="conv">↓ {conversion}</div> : null}
            <button
              type="button"
              className={'stage' + (openStage === stage.key ? ' on' : '')}
              aria-expanded={openStage === stage.key}
              onClick={() => onOpenStage(stage.key, stage.accounts)}
              title={`${stage.definition}. Open to see which accounts.`}
            >
              <span className="name">
                {stage.label}
                <span>{stage.definition}</span>
              </span>
              <span className="track">
                <span
                  className={'bar' + (stage.accounts ? '' : ' empty')}
                  style={{ width: stage.accounts ? barWidth(stage, widest) : '100%' }}
                />
              </span>
              <span className="fig">
                <b className={stage.accounts ? '' : 'zero'}>{formatCount(stage.accounts)}</b>
                <StageFigure stage={stage} />
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
